import React, { MutableRefObject, useEffect, useMemo, useState } from 'react'
import { FormRenderProps } from 'react-final-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { View } from 'react-native'
import { useSelector } from 'react-redux'

import { isNil, isString } from 'lodash-es'
import styled, { css, useTheme } from 'styled-components/native'

import { EntityTypes } from '@lyrahealth-inc/shared-app-logic/src/models/appointmentSummary/AppointmentSummary'
import { Feedback, FeedbackEntityType } from '@lyrahealth-inc/shared-app-logic/src/models/feedback/Feedback'
import { FieldSchema } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { NoteTypes } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { FlyingEnvelopeLottie } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/flyingEnvelopeLottie/FlyingEnvelopeLottie'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { FormBody } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/formBody/FormBody'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { actions, mixpanelEvents } from '../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../mixpanel/mixpanelTracking'
import { getAuthUserId } from '../../data/auth/authSelectors'
import getAIDraftFeedbackMetadata from '../clientNotes/aiDraftFeedbackMetadata'
import { useCreateFeedbackMutation } from '../clientNotes/data/notesApi'

const ContentContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  padding: theme.spacing['16px'],
}))

const ImageContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginLeft: 'auto',
  marginRight: 'auto',
  padding: theme.spacing['24px'],
}))

const ImageHeader = styled(Title)<{ theme: ThemeType }>(({ theme }) => ({
  textAlign: 'center',
  paddingTop: theme.spacing['8px'],
}))

const ImageText = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  textAlign: 'center',
  paddingTop: theme.spacing['8px'],
  maxWidth: '280px',
  marginLeft: 'auto',
  marginRight: 'auto',
}))

const FormButtonContainer = styled.View<{ theme: ThemeType; bgColor: string }>(({ theme, bgColor }) => ({
  backgroundColor: !isNil(bgColor) ? bgColor : theme.colors.backgroundPrimary,
  paddingTop: theme.spacing['16px'],
  right: theme.spacing['16px'],
}))

const PanelFeedbackSection: React.FC<PanelFeedbackSectionProps> = ({
  appointmentId,
  backgroundColor,
  displayTextTime,
  feedbackFormRef,
  entityId,
  entityType,
  loopEnvelopeLottie = true,
  noteType,
  promptType,
  feedback,
  removeTopPadding = false,
  usePerwinkle = false,
  isInitialAIDraft,
}) => {
  const { colors } = useTheme()
  const intl = useIntl()
  const userId = useSelector(getAuthUserId)

  const [displayFeedbackSubmittedSection, setDisplayFeedbackSubmittedSection] = useState(true)
  const [createFeedback, { isSuccess: isSubmitted }] = useCreateFeedbackMutation()

  useEffect(() => {
    if (displayTextTime) {
      const timerId = setTimeout(() => {
        setDisplayFeedbackSubmittedSection(false)
      }, displayTextTime)

      return () => {
        clearTimeout(timerId)
      }
    } else {
      return
    }
  }, [displayTextTime])

  const feedbackResponse = useMemo(() => {
    if (feedback && feedback.length) {
      return feedback.find((response: { entity_id: string }) => {
        return response.entity_id === entityId
      })
    }
    return undefined
  }, [feedback, entityId])

  const entityTypeForFeedback = useMemo(() => {
    switch (entityType) {
      case EntityTypes.ASSIGNMENTS:
        return FeedbackEntityType.CLIENT_INTAKE_SUMMARY
      case EntityTypes.NOTES:
        return noteType === NoteTypes.INITIAL
          ? FeedbackEntityType.NOTE_SUMMARY_PRESENTING_ISSUES
          : FeedbackEntityType.NOTE_SUMMARY_SESSION
      case EntityTypes.AI_DRAFTS:
        return isInitialAIDraft
          ? FeedbackEntityType.PROVIDER_MESSAGE_DRAFT_CLIENT_INTAKE
          : FeedbackEntityType.PROVIDER_MESSAGE_DRAFT_POST_SESSION
      default:
        return undefined
    }
  }, [entityType, isInitialAIDraft, noteType])

  const FormButton = ({
    handleSubmit,
    pristine,
  }: {
    handleSubmit: FormRenderProps['handleSubmit']
    pristine: boolean
  }) => {
    return (
      <FormButtonContainer bgColor={backgroundColor ?? ''}>
        <SecondaryButton
          text={
            <FormattedMessage
              defaultMessage='Submit feedback'
              description='Button to submit feedback on AI notes draft'
            />
          }
          onPress={() => {
            handleSubmit()
          }}
          disabled={pristine}
          testID={tID('PanelFeedbackSection-submitFeedbackButton')}
          accessibilityLabel={intl.formatMessage({
            defaultMessage: 'Submit feedback',
            description: 'Button to submit feedback on AI notes draft',
          })}
          periwinkle={usePerwinkle}
        />
      </FormButtonContainer>
    )
  }
  const { schema, uiSchema } = getAIDraftFeedbackMetadata(usePerwinkle)
  return feedbackResponse || isSubmitted ? (
    displayFeedbackSubmittedSection ? (
      <ContentContainer testID={tID('PanelFeedback-feedbackSubmittedContainer')}>
        <ImageContainer>
          <FlyingEnvelopeLottie loop={loopEnvelopeLottie} />
        </ImageContainer>
        <ImageHeader size={TitleSize.SMALL} text='Feedback submitted' color={colors.textPrimary} />
        <ImageText
          testID={tID('PanelFeedback-feedbackSubmittedText')}
          text='Thank you for the feedback. It will help us improve your experience.'
          size={BodyTextSize.SMALL}
          color={colors.textSecondary}
        />
      </ContentContainer>
    ) : (
      <></>
    )
  ) : (
    <View testID={tID('PanelFeedback-form')}>
      <FormBody
        backgroundColor={backgroundColor}
        name='eligibityForm'
        schema={schema as FieldSchema}
        uiSchema={uiSchema}
        formRef={feedbackFormRef}
        saveForm={({ values }) => {
          track({
            event: mixpanelEvents.BUTTON_PRESS,
            action:
              entityType === EntityTypes.ASSIGNMENTS
                ? actions.CLIENT_INTAKE_FORM_AI_SUMMARY_SUBMIT_FEEDBACK
                : actions.AI_DRAFTS_SUBMIT_FEEDBACK,
            details: {
              entityType: entityTypeForFeedback,
              ...(entityType === EntityTypes.ASSIGNMENTS && { assignmentId: entityId }),
              ...(entityType === EntityTypes.NOTES && { appointmentId }),
              ...(entityType === EntityTypes.AI_DRAFTS && { draftId: entityId }),
            },
          })
          createFeedback({
            feedback: {
              entity_type: entityTypeForFeedback,
              entity_id: entityId ?? '',
              lyra_id: userId,
              user_type: 'provider',
              feedback_response: [
                ...Object.entries(values).map((value: [string, string | number | string[]]) => {
                  return { question: value[0], answer: isString(value[1]) ? value[1] : JSON.stringify(value[1]) }
                }),
                {
                  question: 'promptType',
                  answer: promptType,
                },
              ],
            },
          })
        }}
        intl={intl}
        useMultiSelectTypeAhead
        scrollContainerCustomStyles={{
          formBodyPageContainer: css`
            max-width: 100%;
            ${removeTopPadding ? 'padding-top: 0px;' : ''}
          `,
          scrollContainerContentCustomStyles: {
            padding: 0,
          },
        }}
        formBodyCustomStyles={{
          submitButtonWrapper: css`
            align-self: end;
            max-width: 180px;
          `,
          submitButtonContainer: css`
            background-color: ${backgroundColor}
            border: none;
            box-shadow: none;
            border-top-width: 0;
            padding: 0;
            padding-bottom: ${entityType === EntityTypes.ASSIGNMENTS ? '16px' : ''};
            border-bottom-right-radius: ${entityType === EntityTypes.ASSIGNMENTS ? '16px' : ''};
            border-bottom-left-radius: ${entityType === EntityTypes.ASSIGNMENTS ? '16px' : ''};
          `,
        }}
        formButton={FormButton}
      />
    </View>
  )
}

type PanelFeedbackSectionProps = {
  appointmentId?: string
  backgroundColor?: string
  displayTextTime?: number
  feedbackFormRef?: MutableRefObject<any>
  entityId?: string
  entityType: string
  loopEnvelopeLottie?: boolean
  noteType?: string
  isInitialAIDraft?: boolean
  promptType: string
  feedback?: Feedback[]
  removeTopPadding?: boolean
  usePerwinkle?: boolean
}

export default PanelFeedbackSection
